import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';
import { buildAssetUrl } from '../../utils/api';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import { FaSearch } from 'react-icons/fa';

const getProductId = (p) => p.product_id ?? p.id;
const getProductName = (p) => p.name ?? p.product_name ?? 'Product';

/* ── Moved outside to prevent remount on every render ── */
const ResultList = ({ results, searchQuery, onSelect, onViewAll }) => (
  <>
    {results.map((product) => {
      const id = getProductId(product);
      const name = getProductName(product);
      return (
        <button
          key={id}
          onMouseDown={(e) => { e.preventDefault(); onSelect(product); }}
          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-primary-50"
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-50 p-1">
            {product.image
              ? <img src={buildAssetUrl(product.image)} alt={name} className="h-full w-full object-contain" />
              : <svg className="h-6 w-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            }
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900">{name}</p>
            {product.category_name && <p className="truncate text-xs text-gray-500">{product.category_name}</p>}
          </div>
          {Number(product.price) > 0 && (
            <span className="shrink-0 text-sm font-bold text-primary-600">
              Rs {parseFloat(product.price).toFixed(0)}
            </span>
          )}
        </button>
      );
    })}
    <button
      onMouseDown={(e) => { e.preventDefault(); onViewAll(); }}
      className="flex w-full items-center justify-center gap-2 border-t border-gray-100 px-4 py-3 text-sm font-semibold text-primary-600 hover:bg-primary-50"
    >
      View all results for "{searchQuery}"
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  </>
);

const SearchBar = () => {
  const navigate = useNavigate();
  const { searchQuery, setSearchQuery, searchResults, searchLoading, searchProducts, clearSearch } = useStore();
  const [open, setOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (searchQuery.trim().length >= 2) {
      debounceRef.current = setTimeout(() => { searchProducts(searchQuery); setOpen(true); }, 400);
    } else {
      setOpen(false);
    }
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, searchProducts]);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 639px)');
    const syncViewport = () => setIsMobileViewport(query.matches);

    syncViewport();
    query.addEventListener('change', syncViewport);
    return () => query.removeEventListener('change', syncViewport);
  }, []);

  /* Navigate first, then clear — so route change fires before state wipe */
  const handleSelect = (product) => {
    const id = getProductId(product);
    if (!id) return;
    setOpen(false);
    navigate(`/product/${id}`);
    setTimeout(() => clearSearch(), 100);
  };

  const handleViewAll = () => {
    const q = searchQuery.trim();
    setOpen(false);
    navigate(`/products?q=${encodeURIComponent(q)}`);
    setTimeout(() => clearSearch(), 100);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) { setOpen(false); navigate(`/products?q=${encodeURIComponent(q)}`); }
  };

  const handleClose = () => { setOpen(false); clearSearch(); };

  const hasResults = open && searchResults.length > 0;
  const noResults  = open && !searchLoading && searchQuery.trim().length >= 2 && searchResults.length === 0;

  useBodyScrollLock((hasResults || noResults) && isMobileViewport);

  return (
    <>
      <div ref={wrapperRef} className="relative w-full flex-1">
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              type="text"
              placeholder="Search vegetables, fruits, dairy..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults.length > 0 && setOpen(true)}
              className="w-full rounded-2xl border-2 border-gray-200 px-4 py-2.5 pr-12 text-sm shadow-sm transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-100 sm:py-3 sm:text-base"
            />
            {searchQuery && (
              <button type="button" onClick={() => { clearSearch(); setOpen(false); }}
                className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600">
              {searchLoading
                ? <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                : <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              }
            </button>
          </div>
        </form>

        {/* Desktop dropdown */}
        {(hasResults || noResults) && (
          <div className="absolute left-0 right-0 top-full z-50 mt-2 hidden max-h-80 overflow-y-auto rounded-2xl border border-gray-100 bg-white shadow-2xl sm:block">
            {hasResults
              ? <ResultList results={searchResults} searchQuery={searchQuery} onSelect={handleSelect} onViewAll={handleViewAll} />
              : <p className="px-4 py-6 text-center text-sm text-gray-500">No results for "<span className="font-semibold">{searchQuery}</span>"</p>
            }
          </div>
        )}
      </div>

      {/* Mobile full-screen overlay */}
      {(hasResults || noResults) && (
        <div className="fixed inset-0 z-[80] flex flex-col overflow-hidden bg-white sm:hidden">
          <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
            <form onSubmit={handleSubmit} className="flex-1">
              <div className="relative">
                <input
                  autoFocus
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search vegetables, fruits..."
                  className="w-full rounded-xl border-2 border-primary-500 px-4 py-2.5 pr-10 text-sm focus:outline-none"
                />
                {searchQuery && (
                  <button type="button" onClick={() => clearSearch()}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </form>
            <button onClick={handleClose}
              className="shrink-0 rounded-xl bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200">
              Cancel
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {searchLoading ? (
              <div className="flex items-center justify-center py-16">
                <svg className="h-8 w-8 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              </div>
            ) : hasResults ? (
              <ResultList results={searchResults} searchQuery={searchQuery} onSelect={handleSelect} onViewAll={handleViewAll} />
            ) : (
              <div className="px-4 py-12 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-500">
                  <FaSearch className="h-6 w-6" aria-hidden="true" />
                </div>
                <p className="text-sm text-gray-500">No results for "<span className="font-semibold">{searchQuery}</span>"</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SearchBar;
